import React from 'react';
import { ColumnControlContextProvider } from '@/context/columnControlContext';
import { AdminDataGridContent } from './AdminDataGridContent';

/**
 * DataGrid for admin lists. Takes the rows to show, their loading and error state, and the
 * gridProps from useAdminListParams; other DataGrid props (checkboxSelection, onRowClick, …)
 * pass through. objectType only words the empty and error states.
 *
 * table names the list (the same key useAdminListParams takes). Column order, visibility and
 * widths are saved per table through ColumnControlContextProvider, as in the CRM tables: the
 * toolbar's Columns button reorders, shows and hides columns, and each header's menu can hide
 * its column. Columns with hideable: false can't be hidden and have no column menu.
 * defaultHiddenColumns (fields) start hidden until the admin changes them. toolbarActions go in the toolbar
 * after the Columns button, e.g. an export button. showToolbar={false} leaves the toolbar
 * out, and the column menus with it, for a small read-only table; showColumnsButton={false}
 * keeps the toolbar for its actions but leaves out the Columns button and the column menus.
 * footerContent goes on the left of the footer, with the pagination on the right. With onRowClick, rows get a pointer cursor; pass
 * isRowClickable(row) when only some rows do something on click.
 *
 * By default it pages and sorts the rows it's given in the browser. With no column sort it
 * keeps the rows in the order given, e.g. search relevance from useAdminSearch.
 *
 * For a list paged on the server, pass the current page's rows plus rowCount,
 * paginationMode='server' and sortingMode='server'.
 *
 * Like the audit table, the grid has a fixed maximum height and scrolls inside itself, so
 * the column headers and footer stay in view. maxHeight defaults to the space left under
 * the admin banner, top nav, page header and toolbar; pass a different one if a page has
 * more above the grid. Pass height for a grid that keeps one size however many rows it
 * has (e.g. one whose rows change while it's open), within maxHeight. minHeight (default
 * 12em) keeps a short or empty grid from shrinking below it.
 */
export const AdminDataGrid = ({
	table,
	defaultHiddenColumns,
	maxHeight = 'calc(100vh - 300px)',
	...props
}) => (
	<ColumnControlContextProvider
		variant='admin'
		value={table}
		defaultHiddenColumns={defaultHiddenColumns}
	>
		<AdminDataGridContent maxHeight={maxHeight} {...props} />
	</ColumnControlContextProvider>
);

AdminDataGrid.displayName = 'AdminDataGrid';
